import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio as CKRadio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import ContentfulRichText from '@source-web/contentful-rich-text';
import Button from '@vfuk/core-button';
import ButtonWithIcon from '@vfuk/core-button-with-icon';
import Icon from '@vfuk/core-icon';
import Radio from '@vfuk/core-radio';
import Tooltip from '@vfuk/core-tooltip';
import YoutubeVideo from '@vfuk/core-youtube-video';
import { withPrefix } from 'gatsby';
import { rem } from 'polished';
import { FC, useEffect, useState } from 'react';

import Container from '@/components/Global/Container';
import RadioAsCheckboxGroup from '@/components/pageBuilder/Sections/RadioAsCheckboxGroup';

import { CancelButton, HeadingWrapper, NextButton, RadioWrapper } from './SWConfigureBundleOptions.styles';
import { NextStepProps, SWConfigureBundleOptionsProps } from './SWConfigureBundleOptions.types';

const SWConfigureBundleOptions: FC<SWConfigureBundleOptionsProps> = ({
  id,
  contentful_id,
  productSlug,
  sectionTitle,
  backgroundColor,
  options,
}) => {
  const sectionId = `${id}-${contentful_id}`;
  const [firstChoice, setFirstChoice] = useState<string>('');
  const [secondChoice, setSecondChoice] = useState<string>('');
  const [nextStep, setNextStep] = useState<NextStepProps>({ isEnabled: false });

  useEffect(() => {
    const hasSecondaryChoices = !!options.filter(option => {
      if (
        firstChoice === `${option.id}-${option.contentful_id}` &&
        option.secondaryOptions &&
        option.secondaryOptions.length > 0
      ) {
        return option;
      }
    })[0];

    if (hasSecondaryChoices && secondChoice) {
      const option = options
        .filter(option => `${option.id}-${option.contentful_id}` === firstChoice)[0]
        .secondaryOptions?.filter(option => `${option.id}-${option.contentful_id}` === secondChoice)[0];
      const destination = {
        destinationLink: option?.destinationLink,
        destinationType: option?.destinationType,
      };

      setNextStep({ destination, isEnabled: !!destination.destinationLink && !!destination.destinationType });
    }

    if (!hasSecondaryChoices && firstChoice) {
      setSecondChoice('');
      const { destinationLink, destinationType } = options.filter(
        option => `${option.id}-${option.contentful_id}` === firstChoice
      )[0];
      const destination = {
        destinationLink,
        destinationType,
      };

      setNextStep({ destination, isEnabled: !!destination.destinationLink && !!destination.destinationType });
    }

    if (!firstChoice && !secondChoice) {
      setNextStep({ isEnabled: false });
    }
  }, [firstChoice, secondChoice]);

  return (
    <Box
      as="section"
      id={sectionId}
      backgroundColor={backgroundColor}
      py={{ base: rem('17px'), md: rem('116px'), lg: rem('152px') }}
      h="full"
    >
      <Container>
        <HeadingWrapper direction={{ base: 'column', md: 'row' }} textAlign="center">
          <CancelButton>
            <ButtonWithIcon
              icon={{
                name: 'chevron-left',
                justify: 'left',
              }}
              inverse
              text="Back"
              href={withPrefix(productSlug ? `marketplace/product-list/${productSlug}` : 'marketplace/office-anywhere')}
              appearance="alt1"
            />
          </CancelButton>

          <Flex
            direction="column"
            justify="center"
            mt={{ base: rem('35px'), md: 0 }}
            px={{ md: rem('68px') }}
            textAlign="center"
            w="full"
            sx={{
              'h2, h3': {
                color: 'white',
              },
              h2: {
                fontSize: { base: '4xl', md: 10 },
                lineHeight: { base: rem('41px'), md: rem('46px'), lg: rem('48px') },
                mb: 0,
                whiteSpace: { base: 'normal', lg: 'pre-wrap' },
              },
              h3: {
                fontSize: { base: '2xl', md: rem('29px') },
                letterSpacing: { base: 0, md: 'tight' },
                lineHeight: rem('48px'),
                mb: 0,
                mt: { base: 5, md: rem('38px') },
              },
              p: {
                m: `0 0 ${rem('25px')}`,
                '&:last-child': {
                  m: 0,
                },
              },
              'b, strong': {
                letterSpacing: 'wide',
              },
            }}
          >
            {documentToReactComponents(JSON.parse(sectionTitle.raw))}
          </Flex>

          <CancelButton display={{ base: 'none', md: 'flex' }} visibility="hidden">
            <ButtonWithIcon
              icon={{
                name: 'chevron-left',
                justify: 'right',
              }}
              text="Next"
              appearance="alt1"
            />
          </CancelButton>
        </HeadingWrapper>

        <Flex align="center" direction="column" mt={{ base: rem('18px'), md: 10, lg: rem('34px') }}>
          <Stack align="center" maxW={rem('780px')} spacing={{ base: 13, md: rem('103px') }}>
            <RadioGroup onChange={setFirstChoice} value={firstChoice}>
              <Accordion>
                <Stack spacing={{ base: rem('30px'), md: 12 }}>
                  {options.map(
                    ({
                      id,
                      contentful_id,
                      name,
                      optionTitle,
                      secondaryOptions,
                      videoId,
                      content,
                      infoTextIsImportant,
                      infoText,
                      tooltipText,
                    }) => (
                      <AccordionItem key={`${id}-${contentful_id}`} borderTopWidth="unset">
                        <AccordionButton
                          textAlign="left"
                          px={0}
                          py={0}
                          borderRadius="md"
                          data-selector="bundle options"
                        >
                          <RadioWrapper
                            onClick={() => setFirstChoice(`${id}-${contentful_id}`)}
                            sx={{
                              "[class*='Radiostyle__RadioWrapper-sc']": {
                                boxSize: { base: rem('14px'), md: 5 },
                                minH: 0,
                                mt: { base: rem('1px'), md: rem('3px') },
                              },
                              "[class*='Radiostyle__LabelText-sc']": {
                                borderColor: 'white',
                                boxSize: { base: rem('14px'), md: 5 },
                                '&:before': {
                                  boxSize: { base: rem('14px'), md: 5 },
                                  borderColor: firstChoice === `${id}-${contentful_id}` ? '#00B0CA' : 'white',
                                },
                                '&:after': {
                                  boxSize: { base: 2, md: 3 },
                                  ...(firstChoice === `${id}-${contentful_id}` && { background: '#00B0CA' }),
                                },
                              },
                            }}
                          >
                            <CKRadio
                              as={Radio}
                              groupName={sectionId}
                              value={`${id}-${contentful_id}`}
                              checked={firstChoice === `${id}-${contentful_id}`}
                              alignItems="flex-start"
                              spacing={{ base: 4, md: 7 }}
                              size=""
                            >
                              <Box
                                as="span"
                                color="white"
                                display="block"
                                fontSize={{ base: 'lg', md: '2xl' }}
                                lineHeight={{ base: 5, md: 7 }}
                                pr={{ md: 1 }}
                                sx={{
                                  p: {
                                    m: 0,
                                  },
                                  'b, strong': {
                                    letterSpacing: { base: rem('0.5px'), md: rem('0.7px') },
                                  },
                                }}
                              >
                                {documentToReactComponents(JSON.parse(optionTitle.raw))}
                              </Box>
                            </CKRadio>
                          </RadioWrapper>
                        </AccordionButton>

                        {(secondaryOptions || videoId || content || infoText) && (
                          <AccordionPanel
                            pt={{ base: rem('9px'), md: rem('22px') }}
                            pb={{ base: 0, lg: 1 }}
                            ps={{ base: rem('29px'), lg: rem('46px') }}
                            pe={0}
                          >
                            {secondaryOptions && (
                              <RadioAsCheckboxGroup
                                onChange={setSecondChoice}
                                value={secondChoice}
                                name="secondary-options"
                                options={secondaryOptions.map(option => ({
                                  name: option.name,
                                  value: `${option.id}-${option.contentful_id}`,
                                  children: (
                                    <Stack direction="row" spacing={{ base: 1, lg: 2 }}>
                                      {documentToReactComponents(JSON.parse(option.optionTitle.raw))}

                                      {tooltipText && (
                                        <Popover placement="bottom-end">
                                          <PopoverTrigger>
                                            <Box
                                              sx={{
                                                '[class*="injected-svg"]': {
                                                  stroke: 'white',
                                                  boxSize: { base: 4, md: 5 },
                                                },
                                              }}
                                            >
                                              <Icon name="info-circle" group="system" />
                                            </Box>
                                          </PopoverTrigger>
                                          <PopoverContent>
                                            <PopoverArrow />
                                            <PopoverBody
                                              sx={{
                                                p: {
                                                  fontSize: rem('18px'),
                                                  margin: `${rem('15px')} 0`,
                                                  letterSpacing: '0',
                                                  lineHeight: rem('18px'),
                                                },
                                              }}
                                            >
                                              <ContentfulRichText document={JSON.parse(tooltipText.raw)} />
                                            </PopoverBody>
                                          </PopoverContent>
                                        </Popover>
                                      )}
                                    </Stack>
                                  ),
                                }))}
                                direction={{ base: 'column', sm: 'row' }}
                                mb={{ base: rem('17px'), md: rem('30px') }}
                                sx={{
                                  b: {
                                    letterSpacing: { base: rem('0.8px'), md: rem('1px') },
                                  },
                                  p: {
                                    color: 'white',
                                    fontSize: { base: 'lg', md: '2xl' },
                                    lineHeight: { base: 'shorter', md: rem('26px') },
                                    m: 0,
                                  },
                                }}
                              />
                            )}

                            {videoId && (
                              <Box mb={{ base: 3, md: rem('18px') }}>
                                <YoutubeVideo title={name} videoId={videoId} />
                              </Box>
                            )}

                            {content && (
                              <Box
                                sx={{
                                  h3: {
                                    fontSize: { base: 'lg', md: '2xl' },
                                    lineHeight: { base: 'shorter', md: 7 },
                                    mb: { base: rem('15px'), md: rem('29px') },
                                    color: 'white',
                                  },
                                  span: {
                                    color: 'white',
                                  },
                                  a: {
                                    letterSpacing: { base: rem('1px') },
                                    textDecoration: 'underline',
                                    '&, span': {
                                      color: 'primary1.default',
                                    },
                                  },
                                }}
                              >
                                <ContentfulRichText document={JSON.parse(content.raw)} />
                              </Box>
                            )}

                            {infoText && (
                              <Flex
                                sx={{
                                  p: {
                                    lineHeight: { base: rem('18px') },
                                  },
                                }}
                              >
                                <Box
                                  mr={{ base: rem('9px'), md: 3 }}
                                  visibility={infoTextIsImportant ? 'visible' : 'hidden'}
                                >
                                  <Box
                                    as="span"
                                    boxSize={{ base: rem('10px'), md: 3 }}
                                    color="primary1.default"
                                    display="flex"
                                    fontSize={{ base: rem('26px'), md: rem('34px') }}
                                    lineHeight={{ base: rem('18px'), md: 6 }}
                                    transform="rotate(90deg)"
                                  >
                                    *
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    span: {
                                      color: 'white',
                                    },
                                    p: {
                                      fontSize: { base: 'sm', md: 'md' },
                                      lineHeight: { base: 4, md: rem('18px') },
                                      color: 'white',
                                    },
                                  }}
                                >
                                  <ContentfulRichText document={JSON.parse(infoText.raw)} />
                                </Box>
                              </Flex>
                            )}
                          </AccordionPanel>
                        )}
                      </AccordionItem>
                    )
                  )}
                </Stack>
              </Accordion>
            </RadioGroup>

            <NextButton isEnabled={nextStep.isEnabled}>
              <Button
                text="Next"
                {...(!nextStep.isEnabled && {
                  state: 'disabled',
                })}
                href={
                  nextStep.destination?.destinationType === 'internal' && nextStep.destination?.destinationLink
                    ? withPrefix(nextStep.destination.destinationLink)
                    : nextStep.destination?.destinationLink
                }
                dataSelectorPrefix="next for bundles"
              />
            </NextButton>
          </Stack>
        </Flex>
      </Container>
    </Box>
  );
};

export default SWConfigureBundleOptions;
